import { useEffect, useState } from 'react'
import ga from 'react-ga'
import config from './config'
import logger from './logger'
import * as url from './url'
import Auth from '@getgo/auth-client'

const HIJACKING_ERROR = 'hijacking'
const AUTHORIZATION_ERROR = 'failedauthorization'
const INFLIGHT_REQUEST = 'inflight_request'

export const cleanupSession = () => {
  sessionStorage.removeItem(config().AUTH_STATE_STORAGE_KEY)
  sessionStorage.removeItem(config().AUTH_CODE_VERIFIER_STORAGE_KEY)
  sessionStorage.removeItem(INFLIGHT_REQUEST)
}

export const populateSession = () => {
  const state = auth.randomString(32)
  const codeVerifier = auth.randomString(64)

  sessionStorage.setItem(config().AUTH_STATE_STORAGE_KEY, state)
  sessionStorage.setItem(config().AUTH_CODE_VERIFIER_STORAGE_KEY, codeVerifier)
  sessionStorage.setItem(INFLIGHT_REQUEST, window.location.pathname + window.location.search + window.location.hash)

  return { state, codeVerifier }
}

export const auth = new Auth({
  client_id: config().AUTH_CLIENT_ID,
  url: config().AUTH_SERVICE_URL,
  redirect_url: config().AUTH_CALLBACK_URL,
  enableTokenRefresh: true,
  scope: config().AUTH_SCOPES
})

const usePKCEAuthData = () => {
  const tokenName = config().AUTH_DATA_STORAGE_KEY
  const [token, setToken] = useState(sessionStorage.getItem(tokenName) || null)

  const initiateLoginFlow = () => {
    cleanupSession()
    const { state, codeVerifier } = populateSession()

    setToken(null)

    try {
      auth.loginWithPKCE({
        state,
        code_verifier: codeVerifier
      })
    } catch (error) {
      console.error('[AUTH] Error during login flow:', error)
    }
  }

  useEffect(() => {
    const existingToken = sessionStorage.getItem(tokenName)

    if (existingToken) {
      setToken(existingToken)
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const stateFromLocation = urlParams.get('state')
    const sessionStoredState = sessionStorage.getItem(config().AUTH_STATE_STORAGE_KEY)

    if (stateFromLocation && sessionStoredState) {
      if (sessionStoredState !== stateFromLocation) {
        console.error(HIJACKING_ERROR)
        initiateLoginFlow()
        return
      }

      const authorizationCode = urlParams.get('code')

      if (!authorizationCode) {
        console.error(AUTHORIZATION_ERROR)
        initiateLoginFlow()
        return
      }

      auth
        .requestPKCEToken({
          state: stateFromLocation,
          code: authorizationCode,
          code_verifier: sessionStorage.getItem(config().AUTH_CODE_VERIFIER_STORAGE_KEY),
          client_secret: config().AUTH_CLIENT_SECRET,
        })
        .then((token) => {
          const strToken = JSON.stringify(token)
          sessionStorage.setItem(tokenName, strToken)
          setToken(strToken)

          sessionStorage.removeItem(config().AUTH_STATE_STORAGE_KEY)
          sessionStorage.removeItem(config().AUTH_CODE_VERIFIER_STORAGE_KEY)
          window.history.replaceState({}, document.title, window.location.pathname)

          const inflightRequest = sessionStorage.getItem(INFLIGHT_REQUEST) || config().APP_URL_PREFIX

          if (inflightRequest) {
            sessionStorage.removeItem(INFLIGHT_REQUEST)
            window.location.href = inflightRequest
          } else {
            window.location.reload()
          }
        })
        .catch((err) => {
          console.error('[AUTH] Error requesting PKCE token:', err)
          console.error('[AUTH] Error details:', err.message, err.stack)
        })
    } else {
      initiateLoginFlow()
    }
  }, [tokenName])

  return token
}

export const PARAMS = Object.freeze({
  server: {
    required: ['access_token', 'type', 'expires_in', 'principal'],
    optional: ['state', 'error']
  },
  client: {
    required: ['response_type', 'client_id', 'redirect_uri', 'scope'],
    optional: ['state']
  }
})

export const ERR_MISSING_PARAMETERS = 'Required parameters are missing.'

export const getAuthenticationData = () => {
  const json = sessionStorage.getItem(config().AUTH_DATA_STORAGE_KEY)
  if (!json) {
    return null
  }
  try {
    return JSON.parse(json)
  } catch (err) {
    logger.warn('Local auth data parsing error:', err)
  }
  return null
}

export const logout = () => {
  sessionStorage.removeItem(config().AUTH_DATA_STORAGE_KEY)
  ga.set({ partnerId: null })
  ga.event({
    category: 'Authentication',
    action: 'Logout'
  })
}

export const buildClientRequestParams = ({ inflightRequest }) => ({
  response_type: 'token',
  client_id: config().AUTH_CLIENT_ID,
  redirect_uri: config().AUTH_CALLBACK_URL,
  scope: config().AUTH_SCOPES,
  state: inflightRequest
})

export const getAuthRequestUrl = ({ inflightRequest }) =>
  url.buildUrl({
    url: config().AUTH_SERVICE_URL,
    request: buildClientRequestParams({ inflightRequest }),
    join: '?'
  })

export const retrieveUserPartners = async () => {
  const access_token = getAuthenticationData()?.access_token

  if (!access_token) {
    return []
  }

  const res = await fetch(config().APP_BFF_URL + '/partners', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `bearer ${access_token}`
    }
  }).then(res => {
    return res.json()
  })
  if (res.error) {
    logger.error('[AUTH] Error fetching partners:', res.error)
  }
  return res.partners || []
}

export default usePKCEAuthData